import type { ReactNode } from "react";
import { cx } from "../../components/ui";

/**
 * The small pieces the three AI tabs share.
 *
 * Here rather than in `components/ui` because none of them has a second caller
 * yet, and a primitive promoted before it has one is a guess about what the next
 * page will need. Any of these earns its way up the moment something outside this
 * folder wants it.
 */

/**
 * A segmented control, hairline rather than filled.
 *
 * The dashboard already had this shape twice — the snippet language picker and
 * the chart's series toggle — written inline both times with slightly different
 * padding. One component so a third use does not make a third geometry.
 *
 * `aria-pressed` rather than a radiogroup: these are buttons that change what is
 * displayed, not a value being submitted, and a screen reader reading "pressed"
 * describes that better than a selected radio.
 */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  /** Names the group for a screen reader, e.g. "Date range". */
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex overflow-hidden rounded-md border border-line"
    >
      {options.map((option, index) => (
        <button
          key={String(option.value)}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cx(
            "px-2.5 py-1 text-xs font-medium transition-colors duration-200 ease-smooth",
            index > 0 && "border-l border-line",
            option.value === value
              ? "bg-raised text-fg"
              : "text-fg-subtle hover:bg-raised/60 hover:text-fg",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A filter chip. Same geometry as the capability tags it sits beside, so a
 * togglable thing and a static label do not read as two different systems.
 */
export function Chip({
  children,
  selected,
  onClick,
}: {
  children: ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        "rounded border px-1.5 py-0.5 text-[11px] transition-colors duration-200 ease-smooth",
        selected
          ? "border-accent bg-accent-soft text-fg"
          : "border-line text-fg-subtle hover:border-line-strong hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}

/**
 * One category in the rail, and the first control anybody uses on the catalogue.
 *
 * Deliberately not a `Chip`, though it replaced several. A chip is one condition
 * among many and combines with its neighbours; a category is exclusive, one of
 * eight, and the two reading alike is what made the old row confusing — ticking
 * Text and Image gave a list that was neither, and no chip said so.
 *
 * The shape is what carries that difference. Full width and stacked rather than
 * wrapped into a bag, count ranged right, which reads as a list of shelves to
 * pick one of; the selected row takes the accent rule down its left edge. The
 * marketing marketplace renders the same control over the same catalogue, and
 * somebody who uses both should not have to learn it twice.
 *
 * The count is what earns the width. It answers "is there anything here" before
 * the click rather than after, and it moves with the other filters, so a reader
 * who has already picked a publisher can see which shelves that publisher stocks
 * without visiting each one.
 *
 * A row counting zero is dimmed rather than hidden or disabled. Hidden and the
 * list reshuffles under the cursor as somebody types; disabled and it leaves the
 * tab order, so the one control that would explain the empty table is the one a
 * keyboard cannot reach. Dimmed says "nothing there" and still goes there. The
 * selected row keeps full contrast whatever its count — it is the reason the
 * table is empty, and fading it would hide the answer.
 */
export function CategoryFacet({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        "flex w-full items-center justify-between gap-3 border-l-2 py-1.5 pr-1.5 pl-2 text-left text-[13px]",
        "transition-colors duration-200 ease-smooth",
        "focus-visible:ring-2 focus-visible:ring-accent/20 focus-visible:outline-none",
        selected
          ? "border-l-accent bg-accent-soft font-medium text-fg"
          : count === 0
            ? "border-l-transparent text-fg-subtle hover:bg-raised"
            : "border-l-transparent text-fg-muted hover:bg-raised hover:text-fg",
      )}
    >
      {label}
      <span
        className={cx(
          "font-mono text-[0.6875rem] tabular-nums",
          count === 0 && !selected ? "text-fg-subtle/60" : "text-fg-subtle",
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * One titled block of the filter rail.
 *
 * The rail used to be a single card holding a search box, a row of category tabs
 * and then Publisher, Can and two loose toggles jammed into one wrapping line. At
 * a narrow width that last line collapsed into a jumble where a labelled group
 * and an unlabelled chip were indistinguishable.
 *
 * A hairline and a `micro` title per group is the whole fix. It is also what the
 * marketing marketplace's rail has always done, so the two now read the same way
 * down the page.
 */
export function RailSection({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-line p-4 last:border-b-0">
      {title && <p className="micro mb-2.5">{title}</p>}
      {children}
    </div>
  );
}

/** A static label. The read-only half of `Chip`, at the same size. */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="rounded border border-line px-1.5 py-0.5 text-[11px] text-fg-subtle">
      {children}
    </span>
  );
}

/**
 * What a model charges, whatever it charges by.
 *
 * The catalogue holds four pricing readings and each used to be drawn its own
 * way — two table columns for a rate card, a cell spanning both for per-unit
 * prices, a dash plus a tag where nothing is published, and the word "Free".
 * Four treatments is four things to learn, and it took a tag to explain a column
 * and a paragraph under the table to explain the tag.
 *
 * They are one shape: rows of label→figure, a caption naming the unit, and a
 * note where the charge is not the figures shown. `pricePanel` decides which
 * filling; this only draws it, which is why there is no branch here.
 *
 * Right-aligned and `tabular-nums` so figures line up down the list — that
 * column is the comparison this page exists for. The labels sit to the *left* of
 * their figures rather than above, so a two-row rate card and a three-row unit
 * price occupy the same rhythm and nothing jumps as the reader scans past a
 * shelf boundary.
 */
export function PriceBlock({
  panel,
}: {
  panel: {
    rows: { label: string; value: string }[];
    caption: string | null;
    note: string | null;
    andMore: number;
  };
}) {
  return (
    <dl className="shrink-0 self-start text-right sm:min-w-[9.5rem]">
      {panel.rows.map((row) => (
        <div key={`${row.label}-${row.value}`} className="flex items-baseline justify-end gap-3">
          {/* Empty for "Free" and for a dash, where a label would be inventing a
              distinction the value does not have. */}
          {row.label !== "" && (
            <dt className="text-[11px] text-fg-subtle">{row.label}</dt>
          )}
          <dd className="font-mono text-sm tabular-nums text-fg">{row.value}</dd>
        </div>
      ))}

      {panel.andMore > 0 && (
        <p className="mt-0.5 text-[11px] text-fg-subtle">+{panel.andMore} more</p>
      )}

      {/* The unit, once, under the figures it applies to. */}
      {panel.caption && <p className="mt-1 text-[11px] text-fg-subtle">{panel.caption}</p>}

      {/*
        The basis, and the only place it appears now.
        It used to be a tag in the capability strip on the other side of the row —
        the one tag there that described the price rather than the model, sitting
        among the ones that describe what it does. Here it is attached to the
        figures it qualifies, which is what makes the note under the table
        unnecessary.
      */}
      {panel.note && (
        <p className="mt-1 text-[11px] text-fg-subtle">{panel.note}</p>
      )}
    </dl>
  );
}

/**
 * Share of a total, as a rule under the row it belongs to.
 *
 * A hairline rather than a rounded pill: the page is drawn in hairlines, and a
 * capsule here read as a progress bar — which invites "progress towards what".
 * This is a proportion, and a rule is what a proportion looks like in this
 * vocabulary.
 *
 * A floor of 2% keeps a nearly-free model visible instead of rendering as an
 * empty row, and zero stays genuinely empty so "too small to see" and "nothing"
 * stay distinguishable.
 */
export function ShareRule({ value, total }: { value: number; total: number }) {
  const share = total <= 0 ? 0 : (value / total) * 100;

  return (
    <div className="mt-1.5 h-px w-full bg-line" aria-hidden="true">
      <div
        className="h-px bg-accent/70"
        style={{ width: share === 0 ? "0%" : `${Math.max(2, share)}%` }}
      />
    </div>
  );
}

/**
 * A panel with a hairline heading, for the breakdown tables.
 *
 * `Card` plus `CardHeader` would give a two-line header with a description slot
 * neither breakdown needs, and the extra vertical space matters when two of these
 * sit side by side and their rows should line up.
 */
export function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex min-w-0 flex-col rounded-lg border border-line bg-surface">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h2 className="micro">{title}</h2>
        {action}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}
